import { useState, useEffect } from "react";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger, DialogFooter, DialogDescription } from "./ui/dialog";
import { Input } from "./ui/input";
import { Label } from "./ui/label";
import { Button } from "./ui/button";
import { Textarea } from "./ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "./ui/select";
import { Plus } from "@phosphor-icons/react";
import { api } from "../lib/api";
import { toast } from "sonner";

export const AddPhysicianDialog = ({ onAdded, trigger }) => {
  const [open, setOpen] = useState(false);
  const [specialties, setSpecialties] = useState([]);
  const [loading, setLoading] = useState(false);
  const [form, setForm] = useState({
    full_name: "", npi: "", specialty: "", practice_type: "Independent Clinic",
    practice_name: "", email: "", phone: "", address: "", city: "", state: "", zipcode: "",
    linkedin_url: "", research_experience: "",
    territory: "", bd_rep: "", priority: "Medium", next_action: "", next_action_date: "",
    patient_volume_est: "", emr_system: "",
  });

  useEffect(() => {
    if (open) {
      api.get("/config/specialties").then(r => setSpecialties(r.data.specialties));
    }
  }, [open]);

  const update = (k) => (e) => setForm({ ...form, [k]: e?.target ? e.target.value : e });

  const submit = async () => {
    if (!form.full_name.trim()) { toast.error("Full name required"); return; }
    setLoading(true);
    try {
      await api.post("/physicians", form);
      toast.success("Physician added with default cadence (Day 1, 3, 7)");
      setOpen(false);
      setForm({ full_name: "", npi: "", specialty: "", practice_type: "Independent Clinic", practice_name: "", email: "", phone: "", address: "", city: "", state: "", zipcode: "", linkedin_url: "", research_experience: "", territory: "", bd_rep: "", priority: "Medium", next_action: "", next_action_date: "", patient_volume_est: "", emr_system: "" });
      onAdded?.();
    } catch (e) {
      toast.error("Failed to add physician");
    } finally { setLoading(false); }
  };

  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <DialogTrigger asChild>
        {trigger || (
          <Button data-testid="add-physician-btn" className="bg-[#006CFF] hover:bg-[#0052CC] text-white rounded-none gap-2">
            <Plus size={16} weight="bold" /> Add Physician
          </Button>
        )}
      </DialogTrigger>
      <DialogContent className="max-w-2xl rounded-none border-[#E2E2E5]" data-testid="add-physician-dialog">
        <DialogHeader className="border-b border-[#E2E2E5] pb-3">
          <DialogTitle className="font-display font-black tracking-tight text-2xl">Add New Physician</DialogTitle>
          <DialogDescription className="sr-only">Create a new physician lead. A default Day 1, 3, 7 follow-up cadence will be scheduled.</DialogDescription>
        </DialogHeader>
        <div className="grid grid-cols-2 gap-4 py-2 max-h-[60vh] overflow-y-auto pr-2">
          <div className="col-span-2">
            <Label className="text-xs font-mono uppercase tracking-wider text-[#5C5E66]">Full Name *</Label>
            <Input data-testid="form-full-name" value={form.full_name} onChange={update("full_name")} className="rounded-none mt-1" placeholder="Dr. Jane Smith, MD" />
          </div>
          <div>
            <Label className="text-xs font-mono uppercase tracking-wider text-[#5C5E66]">Specialty</Label>
            <Select value={form.specialty} onValueChange={update("specialty")}>
              <SelectTrigger data-testid="form-specialty" className="rounded-none mt-1"><SelectValue placeholder="Select" /></SelectTrigger>
              <SelectContent>
                {specialties.map(s => <SelectItem key={s} value={s}>{s}</SelectItem>)}
              </SelectContent>
            </Select>
          </div>
          <div>
            <Label className="text-xs font-mono uppercase tracking-wider text-[#5C5E66]">Practice Type</Label>
            <Select value={form.practice_type} onValueChange={update("practice_type")}>
              <SelectTrigger data-testid="form-practice-type" className="rounded-none mt-1"><SelectValue /></SelectTrigger>
              <SelectContent>
                <SelectItem value="Independent Clinic">Independent Clinic</SelectItem>
                <SelectItem value="Hospitalist">Hospitalist</SelectItem>
                <SelectItem value="Hospital">Hospital</SelectItem>
                <SelectItem value="Group Practice">Group Practice</SelectItem>
              </SelectContent>
            </Select>
          </div>
          <div>
            <Label className="text-xs font-mono uppercase tracking-wider text-[#5C5E66]">NPI</Label>
            <Input data-testid="form-npi" value={form.npi} onChange={update("npi")} className="rounded-none mt-1 font-mono" />
          </div>
          <div>
            <Label className="text-xs font-mono uppercase tracking-wider text-[#5C5E66]">Practice Name</Label>
            <Input value={form.practice_name} onChange={update("practice_name")} className="rounded-none mt-1" />
          </div>
          <div>
            <Label className="text-xs font-mono uppercase tracking-wider text-[#5C5E66]">Email</Label>
            <Input data-testid="form-email" type="email" value={form.email} onChange={update("email")} className="rounded-none mt-1" />
          </div>
          <div>
            <Label className="text-xs font-mono uppercase tracking-wider text-[#5C5E66]">Phone</Label>
            <Input value={form.phone} onChange={update("phone")} className="rounded-none mt-1 font-mono" />
          </div>
          <div className="col-span-2">
            <Label className="text-xs font-mono uppercase tracking-wider text-[#5C5E66]">Address</Label>
            <Input value={form.address} onChange={update("address")} className="rounded-none mt-1" />
          </div>
          <div>
            <Label className="text-xs font-mono uppercase tracking-wider text-[#5C5E66]">City</Label>
            <Input value={form.city} onChange={update("city")} className="rounded-none mt-1" />
          </div>
          <div className="grid grid-cols-2 gap-2">
            <div>
              <Label className="text-xs font-mono uppercase tracking-wider text-[#5C5E66]">State</Label>
              <Input value={form.state} onChange={update("state")} className="rounded-none mt-1 font-mono" />
            </div>
            <div>
              <Label className="text-xs font-mono uppercase tracking-wider text-[#5C5E66]">ZIP</Label>
              <Input value={form.zipcode} onChange={update("zipcode")} className="rounded-none mt-1 font-mono" />
            </div>
          </div>
          <div className="col-span-2">
            <Label className="text-xs font-mono uppercase tracking-wider text-[#5C5E66]">LinkedIn URL</Label>
            <Input value={form.linkedin_url} onChange={update("linkedin_url")} className="rounded-none mt-1" />
          </div>
          <div className="col-span-2">
            <Label className="text-xs font-mono uppercase tracking-wider text-[#5C5E66]">Clinical Research Experience</Label>
            <Textarea value={form.research_experience} onChange={update("research_experience")} className="rounded-none mt-1" rows={3} />
          </div>

          {/* BD pipeline fields */}
          <div className="col-span-2 pt-2 pb-1 border-t border-[#E2E2E5]">
            <div className="text-[10px] font-mono uppercase tracking-wider text-[#5C5E66] mt-2">BD Pipeline</div>
          </div>
          <div>
            <Label className="text-xs font-mono uppercase tracking-wider text-[#5C5E66]">Territory</Label>
            <Input data-testid="form-territory" value={form.territory} onChange={update("territory")} className="rounded-none mt-1" placeholder="DFW Expansion" />
          </div>
          <div>
            <Label className="text-xs font-mono uppercase tracking-wider text-[#5C5E66]">BD Rep</Label>
            <Input data-testid="form-bd-rep" value={form.bd_rep} onChange={update("bd_rep")} className="rounded-none mt-1" placeholder="Rep #1" />
          </div>
          <div>
            <Label className="text-xs font-mono uppercase tracking-wider text-[#5C5E66]">Priority</Label>
            <Select value={form.priority} onValueChange={update("priority")}>
              <SelectTrigger data-testid="form-priority" className="rounded-none mt-1"><SelectValue /></SelectTrigger>
              <SelectContent>
                <SelectItem value="High">High</SelectItem>
                <SelectItem value="Medium">Medium</SelectItem>
                <SelectItem value="Low">Low</SelectItem>
              </SelectContent>
            </Select>
          </div>
          <div>
            <Label className="text-xs font-mono uppercase tracking-wider text-[#5C5E66]">Patient Volume Est.</Label>
            <Input value={form.patient_volume_est} onChange={update("patient_volume_est")} className="rounded-none mt-1 font-mono" placeholder="5,000+" />
          </div>
          <div>
            <Label className="text-xs font-mono uppercase tracking-wider text-[#5C5E66]">EMR System</Label>
            <Input value={form.emr_system} onChange={update("emr_system")} className="rounded-none mt-1" placeholder="Epic / Athena / NextGen" />
          </div>
          <div></div>
          <div className="col-span-2">
            <Label className="text-xs font-mono uppercase tracking-wider text-[#5C5E66]">Next Action</Label>
            <Input data-testid="form-next-action" value={form.next_action} onChange={update("next_action")} className="rounded-none mt-1" placeholder="e.g. Send LOI for review" />
          </div>
          <div>
            <Label className="text-xs font-mono uppercase tracking-wider text-[#5C5E66]">Next Action Date</Label>
            <Input type="date" value={form.next_action_date} onChange={update("next_action_date")} className="rounded-none mt-1 font-mono" />
          </div>
        </div>
        <DialogFooter className="border-t border-[#E2E2E5] pt-3">
          <Button variant="outline" className="rounded-none" onClick={() => setOpen(false)}>Cancel</Button>
          <Button data-testid="submit-physician-btn" disabled={loading} onClick={submit} className="bg-[#006CFF] hover:bg-[#0052CC] text-white rounded-none">
            {loading ? "ADDING..." : "Add Physician"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};
